import { useEffect, useState } from "react";
import { Target, CheckCircle2, Sparkles } from "lucide-react";
import { http } from "@/lib/api";
import CompetitionPicker from "@/components/CompetitionPicker";
import PredictionDialog from "@/components/PredictionDialog";
import PredictionTile from "@/components/public/PredictionTile";

const ALL = { code: "ALL", name: "Tutte le competizioni" };

function groupByComp(items) {
  const groups = [];
  const idx = {};
  items.forEach((i) => {
    if (!(i.competition in idx)) { idx[i.competition] = groups.length; groups.push({ name: i.competition_name, code: i.competition, items: [] }); }
    groups[idx[i.competition]].items.push(i);
  });
  return groups;
}

function Grid({ items, onOpen }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {items.map((i, n) => <PredictionTile key={i.match_id} item={i} index={n} onOpen={() => onOpen(i)} />)}
    </div>
  );
}

function Section({ title, icon: Icon, items, empty, onOpen, testid, grouped }) {
  return (
    <section data-testid={testid} className="space-y-5">
      <h3 className="font-display text-3xl font-bold uppercase flex items-center gap-2"><Icon size={22} className="text-[var(--gold)]" /> {title} <span className="font-mono text-sm text-slate-500">{items.length}</span></h3>
      {items.length === 0 ? <p className="text-slate-500 text-sm">{empty}</p> : grouped ? (
        groupByComp(items).map((g) => (
          <div key={g.code} data-testid={`${testid}-group-${g.code}`} className="space-y-3">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-400 border-b border-slate-800 pb-2">{g.name} <span className="text-slate-600">· {g.items.length}</span></div>
            <Grid items={g.items} onOpen={onOpen} />
          </div>
        ))
      ) : <Grid items={items} onOpen={onOpen} />}
    </section>
  );
}

export default function PredictionFeed() {
  const [comp, setComp] = useState(ALL);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    setData(null);
    http.get("/public/feed", { params: { competition: comp.code } }).then((r) => setData(r.data)).catch(() => setData({ upcoming: [], played: [], stats: {} }));
  }, [comp.code]);

  const st = data?.stats;
  const grouped = comp.code === "ALL";
  return (
    <div className="space-y-10">
      <CompetitionPicker value={comp} onChange={setComp} withAll />
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        {[
          ["In programma", st?.upcoming, "feed-stat-upcoming"],
          ["Già giocate", st?.played, "feed-stat-played"],
          ["Precisione AI", st ? (st.accuracy == null ? "—" : `${st.accuracy}%`) : null, "feed-stat-accuracy"],
        ].map(([l, v, id]) => (
          <div key={id} className="panel p-4 sm:p-5">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">{l}</div>
            <div data-testid={id} className="font-display text-3xl sm:text-4xl font-extrabold mt-2">{v ?? "…"}</div>
          </div>
        ))}
      </div>
      {!data ? <div className="panel h-48 scan" /> : (
        <>
          <Section testid="feed-upcoming" title="Prossime partite" icon={Target} items={data.upcoming} grouped={grouped} empty="Nessuna partita in programma in questa competizione." onOpen={setOpen} />
          <Section testid="feed-played" title="Già giocate" icon={CheckCircle2} items={data.played} grouped={grouped} empty="Nessuna partita giocata in questo turno." onOpen={setOpen} />
        </>
      )}
      {open && <PredictionDialog match={{ ...open.match, id: open.match_id, prediction: open.prediction, evaluation: open.evaluation }} code={open.competition} readOnly onClose={() => setOpen(null)} />}
      <p className="text-xs text-slate-500 flex items-center gap-2"><Sparkles size={14} /> Pronostici generati da AI su classifica, forma e statistiche. Solo a scopo informativo.</p>
    </div>
  );
}
